// @ts-strict-ignore
import _ from 'lodash';
import 'other_components/piwik/piwik';
import { sqStateSynchronizer } from '@/core/core.stores';
import { findItemIn, getFormulaTooStores } from '@/trend/trendDataHelper.utilities';
import { logTrackEvent } from '@/utilities/logger';
import { getUserTrackingData, randomInt } from '@/utilities/utilities';
import {
  adminContactEmail,
  isMixpanelEnabled,
  isTelemetryAnonymized,
  isTelemetryEnabled,
} from '@/services/systemConfiguration.utilities';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { isProtractor, throwError } from '@/core/utilities';
import { TrackerService } from '@/track/tracker.service';
import { AnyProperty } from '@/utilities.types';
import { TrackInformation } from '@/track/track.types';
import PiwikTrackingService from '@/track/piwik.service';
import MixpanelTrackingService from '@/track/mixpanel.service';

let trackers = [];
const DEV_CONTRACT = 'DEV';

/**
 * This function calls the function that sends tracking events to Piwik. If we don't know if tracking is enabled or
 * not we make a call to initialize, otherwise we call the executeDoTrack function directly.
 *
 * @param category    - displayed as the 'Category' in the Piwik Console
 * @param action      - displayed as the 'Action' in the Piwik Console
 * @param [information] - displayed as the 'Event' in the Piwik Console
 * @param [dateRange] - optional, indicator whether or not to include the display and investigate
 *   range in the information field.
 */
export function doTrack(
  category: string,
  action: string,
  information?: TrackInformation,
  dateRange?: { displayRange: any; investigateRange: any },
) {
  if (!_.isEmpty(dateRange)) {
    information = `${information} ` ?? '';
    const datePattern = 'MM-DD-YYYY HH:mm:ss.SSS';

    information += `Investigation Range: ${dateRange.investigateRange.start.format(
      datePattern,
    )}-${dateRange.investigateRange.end.format(datePattern)}`;

    information += `Display Range: ${dateRange.displayRange.start.format(
      datePattern,
    )}-${dateRange.displayRange.end.format(datePattern)}`;
  }

  executeDoTrack(category, action, information);
}

export function initializeTrackers(currentUser, darkMode, userLanguage, license) {
  // ensure we destroy existing singleton trackers:
  resetTrackers();

  const { serverEmail, userName, userEmail } = getUserTrackingData({
    anonymizeTelemetry: isTelemetryAnonymized(),
    adminEmail: adminContactEmail(),
    username: currentUser?.username,
    userEmail: currentUser?.email,
  });

  function getContractNumber(): string {
    return _.isEmpty(license?.contractNumber) ? DEV_CONTRACT : license.contractNumber;
  }

  const contractNumber = getContractNumber();
  const companyName = license?.companyName;
  // we want to always send tracking events to Piwik (at least for the time being)
  trackers = [
    new PiwikTrackingService({
      companyName,
      serverEmail,
      userName,
      userEmail,
      contractNumber,
      processPiwikRequest,
    }),
  ];

  if (isMixpanelEnabled() && isTelemetryEnabled()) {
    trackers.push(
      new MixpanelTrackingService({
        companyName,
        serverEmail,
        userName,
        userEmail,
        contractNumber,
        language: userLanguage,
        darkMode,
      }),
    );
  }
}

export function getTrackers(): TrackerService[] {
  if (_.isEmpty(trackers)) {
    throwError('This should not have happened. Make sure you initialize telemetry properly.');
  }
  return trackers;
}

// destroys the singleton instances of the trackers and resets the tracker array.
export function resetTrackers() {
  _.forEach(trackers, (tracker) => tracker.reset());
  trackers = [];
}

/**
 * This function sends tracking events to Piwik.
 *
 * @param category    - displayed as the 'Category' in the Piwik Console
 * @param action      - displayed as the 'Action' in the Piwik Console
 * @param information - displayed as the 'Event' in the Piwik Console
 */
export function executeDoTrack(category: string, action: string, information: string | AnyProperty) {
  // Since the event is logged both locally and directly to piwik a unique event id is stored so they can be de-duped
  const uniqueEventId = randomInt();
  _.forEach(getTrackers(), (tracker) => {
    if (!sqStateSynchronizer.isRehydrating && !isProtractor() && !headlessRenderMode() && tracker.shouldTrack()) {
      tracker.trackEvent(category, action, information, uniqueEventId);
    }
  });
}

/**
 * A Piwik callback that is called whenever a request is about to be made. Always logs the event to a local log
 * with the timestamp so that it can be imported later in case telemetry is disabled or something like an
 * ad-blocker is blocking the Piwik URL.
 *
 * @param {string} request - The request params for recording a Piwik event
 * @return {string} If remote telemetry is disabled then an empty string which will cause Piwik to not issue any
 * request. Otherwise the original request params.
 */
export function processPiwikRequest(request) {
  logTrackEvent(`?${request}&cdt=${getUnixTimestamp()}`);
  if (!isTelemetryEnabled()) {
    return '';
  }

  return request;
}

/**
 * Helper function for generating a UNIX timestamp that can be passed to Piwik, such as for overriding the
 * datetime of the request.
 *
 * @return {number} A UNIX timestamp
 */
function getUnixTimestamp() {
  return Math.round(Date.now() / 1000);
}

/**
 * Gets a JSON blob of data to be used as the 'information' field for tracking formula results
 *
 * @param {Object}  payload - container for arguments
 * @param {String}  payload.id - id of the item (maybe undefined if the formula is new and fails)
 * @param {Boolean} payload.success - true if this was a successful search
 * @param {String}  payload.name - name of the formula
 * @param {String}  payload.formula - text of the formula
 * @param {Object[]} payload.parameters - arguments to the formula
 * @param {String} payload.parameters[].identifier - name of the parameter in the formula
 * @param {Object} payload.parameters[].item - information about the item
 * @param {String} payload.parameters[].item.assets - the list of assets
 * @param {String} payload.parameters[].item.id - item's id
 * @param {String} payload.parameters[].item.name - the name property of the item
 * @param {Boolean} payload.isNew - true if this is a new item (otherwise an update)
 * @param {String} [payload.errorMessage] - if success is false contains the rejection message
 * @return {String} JSON encoded string containing info for tracking
 */
export function trackPowerSearchCompletedInfo(payload) {
  const parameters = _.map(payload.parameters, function (parameter: any) {
    const item = parameter.item;
    const type = _.get(findItemIn(getFormulaTooStores(), item.id), 'itemType');
    return [
      '$',
      parameter.identifier,
      ' = ',
      _.isEmpty(item.assets) ? item.name : `${_.get(item.assets, '[0].formattedName')} > ${item.name}`,
      // If the item is on the chart we know its type otherwise we wont show this info
      type ? ` (type:${type})` : '',
    ].join('');
  });

  doTrack('Workbench_Tool', 'Formula', JSON.stringify(_.assign(_.omit(payload, ['success']), { parameters })));
}
